/**
 * Compute belt groups from belt pairs using union-find.
 * Returns a map of beltId -> { groupSize, isFirst, isLast, groupMembers }
 *
 * @param {number} activeBelts - Number of active belts
 * @param {number[][]} beltPairs - Array of [belt1, belt2] pairs
 * @returns {Object} Map of beltId to group info
 */
export const computeBeltGroups = (activeBelts, beltPairs) => {
  // Union-Find
  const parent = {};
  for (let i = 1; i <= activeBelts; i++) {
    parent[i] = i;
  }

  const find = (x) => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]]; // path compression
      x = parent[x];
    }
    return x;
  };

  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) {
      // Always use the smaller root to keep groups ordered
      if (ra < rb) {
        parent[rb] = ra;
      } else {
        parent[ra] = rb;
      }
    }
  };

  // Union all pairs
  beltPairs.forEach(([a, b]) => {
    if (a <= activeBelts && b <= activeBelts) {
      union(a, b);
    }
  });

  // Build groups: root -> sorted list of members
  const groups = {};
  for (let i = 1; i <= activeBelts; i++) {
    const root = find(i);
    if (!groups[root]) {
      groups[root] = [];
    }
    groups[root].push(i);
  }

  // Build result map
  const result = {};
  for (let i = 1; i <= activeBelts; i++) {
    const root = find(i);
    const members = groups[root];
    const idx = members.indexOf(i);
    result[i] = {
      groupSize: members.length,
      isFirst: idx === 0,
      isLast: idx === members.length - 1,
      groupMembers: members
    };
  }

  return result;
};
